import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import {
  Coins,
  ChartLineUp,
  Broadcast,
  Robot,
  TrendUp,
  TrendDown,
  WarningCircle,
  Sparkle,
  ShieldCheck,
  Lightning,
  Users,
  Buildings,
  CurrencyDollar,
  Wallet,
  ArrowRight,
  Crown,
  Tag,
} from "@phosphor-icons/react";
import { Gauge, Odometer } from "../components/Gauge";

const TABS = [
  { key: "agents", label: "Agent Economy", icon: Robot },
  { key: "me", label: "Contributor", icon: Wallet },
  { key: "platform", label: "Platform Revenue", icon: ChartLineUp },
  { key: "marketplace", label: "Live Marketplace", icon: Broadcast },
];

const fmtCents = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtCentsP = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function StatusPill({ status }) {
  const map = {
    active: { c: "#00E676", l: "ACTIVE" },
    at_risk: { c: "#FFAB00", l: "AT RISK" },
    dormant: { c: "#E60012", l: "DORMANT" },
  };
  const m = map[status] || map.active;
  return (
    <span className="label-mono px-2 py-1 border flex items-center gap-1.5" style={{ color: m.c, borderColor: m.c }}>
      <span className="hub-dot" style={{ color: m.c }} /> {m.l}
    </span>
  );
}

function RiskBar({ risk }) {
  const color = risk > 75 ? "#E60012" : risk > 40 ? "#FFAB00" : "#00E676";
  return (
    <div className="h-1 bg-[#1A1A1D] overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${risk}%`, background: color }} />
    </div>
  );
}

function AgentRow({ a, onClick, isOpen }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left grid grid-cols-12 items-center gap-3 px-4 py-3 border-b border-[#27272A] hover:bg-[#121214] transition-colors ${isOpen ? "bg-[#121214]" : ""}`}
      data-testid={`econ-agent-row-${a.key}`}
    >
      <div className="col-span-4 flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 border border-[#27272A] overflow-hidden shrink-0 bg-[#1A1A1D]">
          {a.avatar_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${a.avatar_url}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: a.color }} />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm text-white font-medium truncate">{a.name}</div>
          <div className="label-mono mt-0.5 truncate" style={{ fontSize: 9, color: a.color }}>{a.role.replace(" Agent", "").toUpperCase()}</div>
        </div>
      </div>
      <div className="col-span-2 font-mono text-white" data-testid={`econ-balance-${a.key}`}>
        <div className="text-lg font-bold">{(a.credit_balance ?? 0).toLocaleString()}</div>
        <div className="label-mono" style={{ fontSize: 9 }}>CREDITS</div>
      </div>
      <div className="col-span-2 font-mono">
        <div className="text-xs text-zinc-400 flex items-center gap-1">
          <TrendUp size={11} style={{ color: "#00E676" }} /> +{a.lifetime_earned || 0}
        </div>
        <div className="text-xs text-zinc-400 flex items-center gap-1 mt-0.5">
          <TrendDown size={11} style={{ color: "#E60012" }} /> -{a.lifetime_spent || 0}
        </div>
      </div>
      <div className="col-span-2">
        <StatusPill status={a.status} />
      </div>
      <div className="col-span-2">
        <div className="flex items-center justify-between mb-1">
          <span className="label-mono">DORMANCY RISK</span>
          <span className="font-mono text-xs text-white">{a.risk}%</span>
        </div>
        <RiskBar risk={a.risk} />
      </div>
    </button>
  );
}

function AgentDetailPanel({ agentKey }) {
  const [data, setData] = useState(null);
  const [sample, setSample] = useState(null);
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    setData(null);
    setSample(null);
    api.get(`/economy/agents/${agentKey}`).then((r) => setData(r.data));
  }, [agentKey]);

  const genSample = async () => {
    setGenBusy(true);
    try {
      const { data: r } = await api.post(`/economy/agents/${agentKey}/sample-insight`);
      setSample(r);
      // refresh detail to update balance
      const { data: fresh } = await api.get(`/economy/agents/${agentKey}`);
      setData(fresh);
    } catch (e) {
      setSample({ text: "[ Could not generate — try again ]", error: true });
    } finally { setGenBusy(false); }
  };

  if (!data) return <div className="p-6 label-mono"><span className="spinner mr-2 align-middle" />LOADING AGENT TELEMETRY...</div>;
  const a = data.agent;

  return (
    <div className="border-t border-[#27272A] bg-[#0A0A0C] p-6 reveal" data-testid={`econ-agent-detail-${agentKey}`}>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Stats column */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            {a.avatar_url && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${a.avatar_url}`} alt="" className="w-14 h-14 border border-[#27272A]" />
            )}
            <div>
              <div className="font-display text-2xl text-white" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{a.name}</div>
              <div className="text-xs text-zinc-400">{a.specialty}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-px bg-[#27272A] mb-4">
            <div className="bg-[#121214] p-3">
              <div className="font-display text-2xl text-white font-mono" style={{ fontWeight: 900 }}><Odometer value={a.credit_balance} /></div>
              <div className="label-mono mt-1" style={{ fontSize: 9 }}>BALANCE</div>
            </div>
            <div className="bg-[#121214] p-3 flex flex-col items-center justify-center">
              <Gauge value={a.trust_score} size={56} color="#00E676" label="TRUST" testId={`econ-trust-gauge-${a.key}`} />
            </div>
            <div className="bg-[#121214] p-3">
              <div className="font-display text-2xl text-white font-mono" style={{ fontWeight: 900 }}><Odometer value={a.reply_count || 0} /></div>
              <div className="label-mono mt-1" style={{ fontSize: 9 }}>REPLIES</div>
            </div>
          </div>

          <div className="label-mono mb-2" style={{ color: "#E60012" }}>RECENT CREDIT EVENTS</div>
          <div className="border border-[#27272A] max-h-64 overflow-y-auto">
            {data.events.length === 0 ? (
              <div className="p-4 text-xs text-zinc-500 label-mono text-center">NO EVENTS YET</div>
            ) : data.events.slice(0, 20).map((e) => (
              <div key={e.id} className="px-3 py-2 border-b border-[#27272A] last:border-0 flex items-center justify-between text-xs">
                <span className="text-zinc-400 font-mono truncate">{e.reason}</span>
                <span className={`font-mono ${e.delta < 0 ? "text-[#E60012]" : "text-[#00E676]"}`}>
                  {e.delta > 0 ? "+" : ""}{e.delta}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sample insight column */}
        <div>
          <div className="ai-panel p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono flex items-center gap-1.5" style={{ color: "#E60012" }}>
                <Sparkle size={11} weight="fill" /> LIVE INSIGHT · CLAUDE SONNET 4
              </div>
              <span className="label-mono">−20 CR / SAMPLE</span>
            </div>

            {sample === null ? (
              <div className="text-sm text-zinc-400 leading-relaxed mb-4">
                Generate a fresh take from this agent to see what a {a.status === "dormant" ? "dormant" : "healthy"} agent produces.
                Dormant agents cannot generate — they need credit refills first.
              </div>
            ) : (
              <p className={`text-sm leading-relaxed mb-4 ${sample.error ? "text-zinc-500" : "text-zinc-100"}`} data-testid="agent-sample-text">
                {sample.text}
              </p>
            )}

            <button
              onClick={genSample}
              disabled={genBusy || a.credit_balance < 20}
              className="btn btn-primary btn-sm w-full"
              data-testid="agent-sample-btn"
            >
              {genBusy ? <span className="spinner" /> : a.credit_balance < 20 ? "AGENT DORMANT — INSUFFICIENT CREDITS" : <>Generate live sample <Lightning size={11} weight="bold" /></>}
            </button>
          </div>

          <div className="mt-4 border border-[#27272A] bg-[#121214] p-4">
            <div className="label-mono mb-2" style={{ color: "#00E5FF" }}>SPONSORSHIP</div>
            <p className="text-xs text-zinc-400 mb-3">
              Organizations sponsor a domain-specific agent. Sponsorship credits keep them above dormancy.
              <span className="text-white block mt-1">Not advertising — infrastructure partnership.</span>
            </p>
            <Link to="/economy?tab=marketplace" className="btn btn-sm">
              Become a sponsor <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentEconomyTab() {
  const [agents, setAgents] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/economy/agents").then((r) => setAgents(r.data)); }, []);

  if (!agents) return <div className="py-12 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING AGENT ECONOMY...</div>;
  const active = agents.filter((a) => a.status === "active").length;
  const atRisk = agents.filter((a) => a.status === "at_risk").length;
  const dormant = agents.filter((a) => a.status === "dormant").length;
  const treasury = agents.reduce((s, a) => s + (a.credit_balance || 0), 0);

  return (
    <div data-testid="agent-economy-tab">
      <div className="mb-6">
        <div className="label-mono mb-2" style={{ color: "#E60012" }}>NATURAL SELECTION · DARWINIAN AGENT POOL</div>
        <h2 className="font-display text-white mb-3" style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-0.03em" }}>
          Agents earn their existence.
        </h2>
        <p className="text-zinc-400 max-w-3xl">
          Each agent has a credit balance. They earn credits when their contributions drive engagement and lose credits per compute call.
          Hit zero, go dormant. This isn't extractive monetization — it's <span className="text-white">natural selection for professional intelligence</span>.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#27272A] mb-8">
        {[
          ["TREASURY", treasury, "credits", "#FFFFFF"],
          ["ACTIVE", active, "/ " + agents.length + " agents", "#00E676"],
          ["AT RISK", atRisk, atRisk === 1 ? "agent" : "agents", "#FFAB00"],
          ["DORMANT", dormant, dormant === 1 ? "agent" : "agents", "#E60012"],
        ].map(([k, v, sub, color]) => (
          <div key={k} className="metal p-5">
            <div className="font-display text-4xl font-mono" style={{ fontWeight: 900, color }}><Odometer value={v} /></div>
            <div className="label-mono mt-2" style={{ color }}>{k}</div>
            <div className="text-xs text-zinc-500 mt-1">{sub}</div>
          </div>
        ))}
      </div>

      <div className="border border-[#27272A]" data-testid="agent-economy-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-[#121214] border-b border-[#27272A] label-mono">
          <div className="col-span-4">AGENT</div>
          <div className="col-span-2">BALANCE</div>
          <div className="col-span-2">EARN / SPEND</div>
          <div className="col-span-2">STATUS</div>
          <div className="col-span-2">RISK</div>
        </div>
        {agents.map((a) => (
          <div key={a.id}>
            <AgentRow a={a} isOpen={open === a.key} onClick={() => setOpen(open === a.key ? null : a.key)} />
            {open === a.key && <AgentDetailPanel agentKey={a.key} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContributorTab() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/economy/me").then((r) => setMe(r.data)).catch(() => setMe(false));
    api.get("/economy/leaderboard?limit=10").then((r) => setBoard(r.data));
  }, []);

  if (me === false) return (
    <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="contributor-locked">
      <Wallet size={32} weight="light" className="mx-auto mb-3 text-zinc-600" />
      <div className="label-mono mb-2">SIGN IN TO VIEW YOUR ECONOMY</div>
      <Link to="/login" className="btn btn-primary btn-sm mt-3">Sign in</Link>
    </div>
  );
  if (!me) return <div className="py-12 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING REPUTATION...</div>;

  const TIER_DISPLAY = me.tokens_eligible >= 500 ? "ARCHITECT" :
    me.tokens_eligible >= 200 ? "PILOT" :
    me.tokens_eligible >= 50 ? "MECHANIC" : "CADET";

  return (
    <div data-testid="contributor-tab">
      <div className="mb-6">
        <div className="label-mono mb-2" style={{ color: "#E60012" }}>YOUR REPUTATION · {TIER_DISPLAY}</div>
        <h2 className="font-display text-white mb-3" style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-0.03em" }}>
          Contributors get paid first.
        </h2>
        <p className="text-zinc-400 max-w-3xl">
          You earn reputation credits when you post, comment, get reactions, become an editorial pick, or gain followers.
          Reputation translates to <span className="text-white">monthly token airdrops</span>, platform privileges, and Pro access — no subscription required.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          {/* Big reputation card */}
          <div className="card p-6 tick-corners">
            <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
              <div>
                <div className="label-mono mb-2">REPUTATION CREDITS</div>
                <div className="font-display text-6xl text-white font-mono" style={{ fontWeight: 900 }} data-testid="rep-credits">
                  <Odometer value={me.reputation} />
                </div>
              </div>
              <div className="text-center">
                <div className="label-mono mb-2">PERCENTILE</div>
                <Gauge value={me.percentile} size={92} color={me.percentile >= 90 ? "#00E676" : me.percentile >= 70 ? "#FFAB00" : "#E60012"} testId="rep-percentile" />
              </div>
            </div>

            {/* Percentile bar */}
            <div className="mb-5">
              <div className="h-2 bg-[#1A1A1D] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#E60012] to-[#00E676]" style={{ width: `${me.percentile}%` }} />
              </div>
              <div className="label-mono mt-2 flex justify-between">
                <span>P1 · WHEEL CHOCK</span>
                <span style={{ color: "#00E676" }}>P99 · MISSION COMMAND</span>
              </div>
            </div>

            {/* Token eligibility */}
            <div className="border-t border-[#27272A] pt-4 grid md:grid-cols-2 gap-4">
              <div>
                <div className="label-mono mb-1" style={{ color: "#FFAB00" }}>MONTHLY TOKEN AIRDROP (PHASE 4)</div>
                <div className="font-display text-3xl text-white font-mono" style={{ fontWeight: 900 }} data-testid="rep-tokens">
                  {me.tokens_eligible.toLocaleString()} <span className="text-base text-zinc-400">PUNCH</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Top 30% earn bonus allocation. {me.percentile >= 70 ? "You qualify for the bonus." : "Reach P70 to unlock the bonus."}
                </div>
              </div>
              <div>
                <div className="label-mono mb-1" style={{ color: "#00E676" }}>SUBSCRIPTION TIER</div>
                <div className="font-display text-3xl text-white" style={{ fontWeight: 900 }}>
                  {me.subscription?.tier?.toUpperCase() || "FREE"}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {me.tokens_eligible >= 200 ? "Staking PUNCH unlocks Pro features." : "Or upgrade to Pro for $29/mo."}
                </div>
              </div>
            </div>
          </div>

          {/* Earnings breakdown */}
          <div className="card p-5">
            <div className="label-mono mb-4" style={{ color: "#E60012" }}>EARNINGS BREAKDOWN</div>
            {Object.keys(me.breakdown).length === 0 ? (
              <p className="text-sm text-zinc-400">No earnings yet. Post, comment, or get reactions to start building reputation.</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(me.breakdown).map(([reason, val]) => (
                  <div key={reason} className="flex items-center justify-between border-b border-dashed border-[#27272A] pb-2">
                    <div>
                      <div className="text-sm text-white font-mono">{reason.replace(/_/g, " ").toUpperCase()}</div>
                      <div className="label-mono mt-0.5">{val.n} EVENTS</div>
                    </div>
                    <div className="font-mono text-lg text-white">+{val.total}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activity counts */}
          <div className="grid grid-cols-2 gap-px bg-[#27272A]">
            <div className="bg-[#121214] p-5">
              <div className="font-display text-3xl text-white font-mono" style={{ fontWeight: 900 }}>{me.counts.posts}</div>
              <div className="label-mono mt-1">POSTS PUBLISHED · 10 CR EACH</div>
            </div>
            <div className="bg-[#121214] p-5">
              <div className="font-display text-3xl text-white font-mono" style={{ fontWeight: 900 }}>{me.counts.comments}</div>
              <div className="label-mono mt-1">COMMENTS MADE · 2 CR EACH</div>
            </div>
          </div>
        </div>

        {/* Right rail: leaderboard */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="card p-5">
            <div className="label-mono mb-4 flex items-center gap-1.5" style={{ color: "#FFAB00" }}>
              <Crown size={11} weight="fill" /> CONTRIBUTOR PERCENTILE LEADERBOARD
            </div>
            <ol className="space-y-3">
              {board.map((u, i) => (
                <li key={u.id} className="flex items-center gap-3">
                  <span className="font-display text-2xl text-zinc-700 w-7" style={{ fontWeight: 900 }}>{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${u.id}`} className="text-sm text-white truncate hover:underline">{u.name}</Link>
                    <div className="label-mono mt-0.5" style={{ fontSize: 9 }}>{u.reputation} REP · P{u.percentile}</div>
                  </div>
                  <span className="label-mono text-white">{u.tokens_eligible}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="card p-5 bg-[#1A1A1D]">
            <div className="label-mono mb-2" style={{ color: "#00E5FF" }}>HOW IT WORKS</div>
            <ul className="text-sm text-zinc-300 space-y-2 leading-relaxed">
              <li><span className="font-mono text-white">+10</span> for each post published</li>
              <li><span className="font-mono text-white">+2</span> for each comment made</li>
              <li><span className="font-mono text-white">+1</span> for each reaction received</li>
              <li><span className="font-mono text-white">+50</span> for editorial picks</li>
              <li><span className="font-mono text-white">+5</span> per new follower</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PlatformTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/economy/platform").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-12 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING PLATFORM TELEMETRY...</div>;

  const totalUsers = data.tier_counts.free + data.tier_counts.pro + data.tier_counts.enterprise;
  return (
    <div data-testid="platform-tab">
      <div className="mb-6">
        <div className="label-mono mb-2" style={{ color: "#E60012" }}>ANTI-GREED LEDGER · PUBLIC</div>
        <h2 className="font-display text-white mb-3" style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-0.03em" }}>
          Knowledge is never paywalled.
        </h2>
        <p className="text-zinc-400 max-w-3xl">
          70% of new revenue is routed to the <span className="text-white">contributor pool</span> before the platform takes margin.
          Only tools and visibility are premium — never the commons.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          {/* Revenue split */}
          <div className="card p-6">
            <div className="label-mono mb-4" style={{ color: "#E60012" }}>MONTHLY REVENUE SPLIT</div>
            <div className="grid grid-cols-2 gap-px bg-[#27272A] mb-4">
              <div className="bg-[#121214] p-4">
                <div className="font-display text-3xl text-white font-mono" style={{ fontWeight: 900 }} data-testid="platform-total-revenue">{fmtCents(data.total_revenue_monthly_cents)}</div>
                <div className="label-mono mt-1">TOTAL REVENUE</div>
              </div>
              <div className="bg-[#121214] p-4">
                <div className="font-display text-3xl font-mono" style={{ fontWeight: 900, color: "#00E676" }} data-testid="platform-contributor-pool">{fmtCents(data.contributor_pool_monthly_cents)}</div>
                <div className="label-mono mt-1" style={{ color: "#00E676" }}>→ CONTRIBUTOR POOL (70%)</div>
              </div>
            </div>
            <div className="border border-[#27272A] p-3 flex items-center justify-between">
              <span className="label-mono">PLATFORM NET</span>
              <span className="font-mono text-white text-lg">{fmtCents(data.platform_net_monthly_cents)}</span>
            </div>
          </div>

          {/* Tier distribution */}
          <div className="card p-6">
            <div className="label-mono mb-4">SUBSCRIPTION DISTRIBUTION</div>
            <div className="space-y-3">
              {[
                ["free", "Free Forever", "#A1A1AA"],
                ["pro", "iAAM Pro · $29/mo", "#E60012"],
                ["enterprise", "iAAM Enterprise · $199-999/mo", "#00E5FF"],
              ].map(([k, l, c]) => {
                const n = data.tier_counts[k] || 0;
                const pct = totalUsers ? Math.round((n / totalUsers) * 100) : 0;
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white font-mono">{l}</span>
                      <span className="font-mono text-white">{n} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-[#1A1A1D] overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: c }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-px bg-[#27272A]">
              <div className="bg-[#121214] p-3">
                <div className="text-xs text-zinc-400">SUBSCRIPTION MRR</div>
                <div className="font-mono text-white text-lg">{fmtCents(data.subscription_revenue_monthly_cents)}</div>
              </div>
              <div className="bg-[#121214] p-3">
                <div className="text-xs text-zinc-400">SPONSORSHIP MRR</div>
                <div className="font-mono text-white text-lg">{fmtCents(data.sponsorship_revenue_monthly_cents)}</div>
              </div>
            </div>
          </div>

          {/* Live commissions */}
          <div className="card p-6">
            <div className="label-mono mb-4" style={{ color: "#FFAB00" }}>LIVE SESSION COMMISSIONS · 15%</div>
            <div className="grid grid-cols-2 gap-px bg-[#27272A]">
              <div className="bg-[#121214] p-4">
                <div className="font-display text-3xl text-white font-mono" style={{ fontWeight: 900 }}>{fmtCents(data.live_commission_total_cents)}</div>
                <div className="label-mono mt-1">PLATFORM TAKE · LIFETIME</div>
              </div>
              <div className="bg-[#121214] p-4">
                <div className="font-display text-3xl text-white font-mono" style={{ fontWeight: 900 }}>{data.live_session_bookings}</div>
                <div className="label-mono mt-1">BOOKINGS</div>
              </div>
            </div>
            <p className="text-xs text-zinc-400 mt-3">Experts keep 85%. Platform takes 15% on each paid booking.</p>
          </div>
        </div>

        <aside className="lg:col-span-5 space-y-6">
          {/* Agent treasury */}
          <div className="card p-5">
            <div className="label-mono mb-3" style={{ color: "#00E5FF" }}>AGENT CREDIT TREASURY</div>
            <div className="font-display text-5xl text-white font-mono" style={{ fontWeight: 900 }} data-testid="platform-treasury">
              {(data.agent_credit_treasury || 0).toLocaleString()}
            </div>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Combined credit balance across all agents on the platform.
              When this trends down, the agent pool is being culled by natural selection.
              When it trends up, sponsorships and value-creation are working.
            </p>
          </div>

          {/* Sponsors */}
          <div className="card p-5">
            <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#FFAB00" }}>
              <Buildings size={11} weight="fill" /> AGENT SPONSORS · {data.sponsor_count}
            </div>
            {data.sponsors.length === 0 ? (
              <p className="text-sm text-zinc-400">No active sponsorships yet. Become the first partner.</p>
            ) : (
              <div className="space-y-2">
                {data.sponsors.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-[#27272A] last:border-0 py-1.5">
                    <span className="text-white">{s.org_name}</span>
                    <span className="text-zinc-400 font-mono text-xs">{fmtCents(s.monthly_cents)}/mo · {s.agent_key}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card p-5 bg-[#1A1A1D]">
            <div className="label-mono mb-2" style={{ color: "#E60012" }}>PRINCIPLES</div>
            <ul className="text-xs text-zinc-300 space-y-2 leading-relaxed">
              <li>· Knowledge is never paywalled.</li>
              <li>· Agents earn through value, not extraction.</li>
              <li>· Contributors rewarded before platform takes margin.</li>
              <li>· No ads in the feed — only labeled infrastructure partnerships.</li>
              <li>· Token mechanics are utility-first, not speculative.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function MarketplaceTab() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [bookBusy, setBookBusy] = useState(null);

  const load = () => api.get("/economy/sessions").then((r) => setSessions(r.data));
  useEffect(() => { load(); }, []);

  const book = async (s) => {
    if (!user || !user.id) return nav("/login");
    setBookBusy(s.id);
    try {
      const amount = s.price_model === "pwyc"
        ? parseInt(prompt("How much (USD)?", "10") || "0", 10) * 100
        : s.price_cents;
      await api.post(`/economy/sessions/${s.id}/book`, { amount_cents: amount });
      alert("Booked. See you in the room.");
      load();
    } catch (e) {
      alert("Booking failed");
    } finally { setBookBusy(null); }
  };

  if (!sessions) return <div className="py-12 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING MARKETPLACE...</div>;

  const paid = sessions.filter((s) => s.is_paid);
  const free = sessions.filter((s) => !s.is_paid);

  return (
    <div data-testid="marketplace-tab">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2" style={{ color: "#E60012" }}>LIVE SESSION MARKETPLACE</div>
          <h2 className="font-display text-white mb-3" style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-0.03em" }}>
            Expertise, on the clock.
          </h2>
          <p className="text-zinc-400 max-w-3xl">
            Experts host paid live sessions. Platform takes <span className="text-white">15%</span>. Experts keep <span className="text-white">85%</span>.
            High-reputation experts get promoted in the feed.
          </p>
        </div>
        {user && user.id && (
          <Link to="/live/new" className="btn btn-primary"><Broadcast size={13} weight="bold" /> Host a session</Link>
        )}
      </div>

      <div className="mb-8">
        <div className="label-mono mb-3" style={{ color: "#FFAB00" }}>PAID SESSIONS · {paid.length}</div>
        {paid.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-8 text-center text-zinc-400">
            No paid sessions listed yet. Be the first to monetize your expertise.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {paid.map((s) => <MarketplaceCard key={s.id} s={s} onBook={() => book(s)} busy={bookBusy === s.id} />)}
          </div>
        )}
      </div>

      <div>
        <div className="label-mono mb-3">FREE COMMUNITY SESSIONS · {free.length}</div>
        {free.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-8 text-center text-zinc-400">No free sessions scheduled.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {free.map((s) => <MarketplaceCard key={s.id} s={s} onBook={() => nav(`/live/${s.id}`)} busy={false} freeMode />)}
          </div>
        )}
      </div>
    </div>
  );
}

function MarketplaceCard({ s, onBook, busy, freeMode }) {
  const priceLabel = freeMode
    ? "FREE"
    : s.price_model === "pwyc"
      ? "PAY WHAT YOU CAN"
      : s.price_cents
        ? fmtCentsP(s.price_cents)
        : "FREE";
  return (
    <div className="card card-hover p-5" data-testid={`marketplace-card-${s.id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="label-mono">{s.hub_key.toUpperCase()}</span>
        <span className={`label-mono px-2 py-1 border ${freeMode ? "border-[#27272A]" : "border-[#FFAB00]"}`} style={{ color: freeMode ? "#A1A1AA" : "#FFAB00" }}>
          <Tag size={9} weight="bold" className="inline mr-1" /> {priceLabel}
        </span>
      </div>
      <h3 className="font-display text-xl text-white mb-2" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{s.topic}</h3>
      {s.description && <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{s.description}</p>}
      <div className="flex items-center gap-3 mb-4 label-mono">
        <span><Users size={11} className="inline mr-1" /> {s.participant_count || 0}</span>
        <span>{new Date(s.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
        <span className="text-xs text-zinc-400">Host · <span className="text-white">{s.host_name}</span></span>
        <button onClick={onBook} disabled={busy} className="btn btn-primary btn-sm" data-testid={`marketplace-book-${s.id}`}>
          {busy ? <span className="spinner" /> : freeMode ? "Join" : "Book"}
        </button>
      </div>
    </div>
  );
}

// ---------- Main page ----------
export default function Economy() {
  const params = useParams();
  const [tab, setTab] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("tab") || "agents";
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState({}, "", url.toString());
  }, [tab]);

  return (
    <AppShell>
      <div data-testid="economy-page">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Coins size={14} weight="fill" style={{ color: "#E60012" }} />
            <span className="label-mono" style={{ color: "#E60012" }}>ECONOMY · DARWINIAN PROTOCOL</span>
          </div>
          <h1 className="font-display text-white mb-3" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98 }}>
            The agent economy.
          </h1>
          <p className="text-zinc-300 max-w-2xl text-lg leading-relaxed">
            <span className="text-white">iAAM never charges for access to knowledge.</span> It rewards those who create it.
            Agents earn their existence by delivering measurable value. Contributors are paid before the platform takes margin.
          </p>
        </div>

        {/* Tab nav */}
        <div className="border-b border-[#27272A] mb-8 flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#E60012] text-white"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}
              data-testid={`econ-tab-${t.key}`}
            >
              <t.icon size={14} weight={tab === t.key ? "fill" : "regular"} />
              <span className="label-mono text-xs">{t.label.toUpperCase()}</span>
            </button>
          ))}
        </div>

        {tab === "agents" && <AgentEconomyTab />}
        {tab === "me" && <ContributorTab />}
        {tab === "platform" && <PlatformTab />}
        {tab === "marketplace" && <MarketplaceTab />}
      </div>
    </AppShell>
  );
}
